import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { INTRO_SOUND_URL } from "../data/content";

const SESSION_KEY = "mm-intro-played";

/**
 * Plays a short mariachi trumpet intro on the first user interaction.
 * Shows a discreet floating mute/unmute button while playing.
 * Only plays once per session.
 */
export const IntroSound = () => {
    const audioRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [muted, setMuted] = useState(false);
    const [playing, setPlaying] = useState(false);
    const triggeredRef = useRef(false);

    useEffect(() => {
        // If already played this session, skip.
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem(SESSION_KEY) === "1") return;

        const tryPlay = () => {
            if (triggeredRef.current) return;
            triggeredRef.current = true;
            const a = audioRef.current;
            if (!a) return;
            try {
                a.volume = 0.55;
                a.muted = false;
                const p = a.play();
                if (p && typeof p.then === "function") {
                    p.then(() => {
                        setPlaying(true);
                        setVisible(true);
                        sessionStorage.setItem(SESSION_KEY, "1");
                    }).catch(() => {
                        // autoplay still blocked — silently give up
                    });
                }
            } catch (_) {}
            cleanup();
        };

        const onEnded = () => {
            setPlaying(false);
            // hide after small delay
            setTimeout(() => setVisible(false), 600);
        };

        const events = ["click", "touchstart", "keydown", "scroll"];
        events.forEach((ev) =>
            window.addEventListener(ev, tryPlay, { once: true, passive: true })
        );

        const a = audioRef.current;
        if (a) a.addEventListener("ended", onEnded);

        function cleanup() {
            events.forEach((ev) => window.removeEventListener(ev, tryPlay));
        }

        return () => {
            cleanup();
            if (a) a.removeEventListener("ended", onEnded);
        };
    }, []);

    const toggleMute = () => {
        const a = audioRef.current;
        if (!a) return;
        a.muted = !a.muted;
        setMuted(a.muted);
        if (a.muted) {
            a.pause();
            setPlaying(false);
            setTimeout(() => setVisible(false), 400);
        }
    };

    return (
        <>
            <audio ref={audioRef} src={INTRO_SOUND_URL} preload="auto" crossOrigin="anonymous" />
            {visible && (
                <button
                    type="button"
                    onClick={toggleMute}
                    data-testid="intro-sound-btn"
                    aria-label={muted ? "Activar sonido" : "Silenciar"}
                    className="fixed left-5 top-24 z-[55] inline-flex items-center gap-2 rounded-full border border-gold/50 bg-background/85 px-4 py-2 backdrop-blur-md transition hover:border-gold sm:left-6"
                >
                    {muted ? (
                        <VolumeX className="h-4 w-4 text-gold" />
                    ) : (
                        <Volume2 className="h-4 w-4 text-gold" />
                    )}
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
                        {playing ? "Sonando…" : muted ? "Silenciado" : "Bienvenida"}
                    </span>
                    {/* equaliser animation */}
                    {playing && !muted && (
                        <span className="ml-1 flex items-end gap-0.5">
                            {[0, 1, 2].map((i) => (
                                <span
                                    key={i}
                                    className="w-0.5 rounded-sm bg-gold"
                                    style={{
                                        height: 6 + (i % 2) * 4,
                                        animation: `introBar 0.6s ease-in-out ${i * 0.15}s infinite alternate`,
                                    }}
                                />
                            ))}
                        </span>
                    )}
                    <style>{`
                        @keyframes introBar {
                            from { transform: scaleY(0.5); }
                            to { transform: scaleY(1.6); }
                        }
                    `}</style>
                </button>
            )}
        </>
    );
};

export default IntroSound;
